// Create Investor Dialog
// One-click investor creation: Auth + User + Lender

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import { createInvestorAccount, CreateInvestorData } from '../../services/userService';

interface CreateInvestorDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateInvestorDialog: React.FC<CreateInvestorDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreateInvestorData>({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    address: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    bankCode: '',
    branchCode: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (field: keyof CreateInvestorData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.email || !formData.password || !formData.fullName) {
      setError('Email, password, and full name are required');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call the userService to create everything
      const result = await createInvestorAccount(formData);

      // Clear password from memory immediately after use
      setFormData(prev => ({ ...prev, password: '' }));
      
      setSuccess(true);

      // Wait a moment to show success, then close
      setTimeout(() => {
        if (onSuccess) onSuccess();
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to create investor account');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        email: '',
        password: '',
        fullName: '',
        phone: '',
        address: '',
        bankName: '',
        accountName: '',
        accountNumber: '',
        bankCode: '',
        branchCode: '',
      });
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAdd sx={{ color: '#FFD700' }} />
          <Typography variant="h6">Create New Investor</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Automatically creates: Firebase Auth + User Profile + Lender Profile
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            ✅ Investor account created successfully!<br/>
            ⚠️ You have been logged out (Firebase limitation).<br/>
            Please login again as admin to continue.
          </Alert>
        )}

        {/* Authentication Section */}
        <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1, mt: 2 }}>
          Login Credentials
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            required
            fullWidth
            disabled={loading || success}
          />
          <TextField
            label="Temporary Password"
            type="password"
            value={formData.password}
            onChange={handleChange('password')}
            required
            fullWidth
            helperText="Minimum 6 characters. Investor should change this after first login."
            disabled={loading || success}
          />
        </Box>

        {/* Personal Information Section */}
        <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
          Personal Information
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
          <TextField
            label="Full Name"
            value={formData.fullName}
            onChange={handleChange('fullName')}
            required
            fullWidth
            disabled={loading || success}
          />
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={handleChange('phone')}
            fullWidth
            placeholder="+852 1234 5678"
            disabled={loading || success}
          />
          <TextField
            label="Address"
            value={formData.address}
            onChange={handleChange('address')}
            fullWidth
            multiline
            rows={2}
            disabled={loading || success}
          />
        </Box>

        {/* Bank Details Section */}
        <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
          Bank Details
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Bank Name"
            value={formData.bankName}
            onChange={handleChange('bankName')}
            fullWidth
            disabled={loading || success}
          />
          <TextField
            label="Account Name"
            value={formData.accountName}
            onChange={handleChange('accountName')}
            fullWidth
            disabled={loading || success}
          />
          <TextField
            label="Account Number"
            value={formData.accountNumber}
            onChange={handleChange('accountNumber')}
            fullWidth
            disabled={loading || success}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Bank Code (Optional)"
              value={formData.bankCode}
              onChange={handleChange('bankCode')}
              disabled={loading || success}
            />
            <TextField
              label="Branch Code (Optional)"
              value={formData.branchCode}
              onChange={handleChange('branchCode')}
              disabled={loading || success}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || success}
          sx={{
            bgcolor: '#FFD700',
            color: '#000',
            '&:hover': { bgcolor: '#FFC700' },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#000' }} />
          ) : success ? (
            'Created!'
          ) : (
            'Create Investor Account'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvestorDialog;
